import React, { useState } from 'react';
import { Job, TreeSurvey as TreeSurveyType } from '../types';
import { Icons } from '../constants';
import { analyzeLocation } from '../services/gemini';

interface Props {
  job?: Job; // Optional, if launched from a job
  onComplete: (survey: Omit<TreeSurveyType, 'id' | 'createdAt'>) => void;
  onClose: () => void;
}

const TreeSurvey: React.FC<Props> = ({ job, onComplete, onClose }) => {
  const [clientName, setClientName] = useState(job?.clientName || '');
  const [clientPhone, setClientPhone] = useState(job?.phone || '');
  const [clientEmail, setClientEmail] = useState(job?.email || '');
  const [surveyorName, setSurveyorName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [siteAddress, setSiteAddress] = useState(job?.address || '');
  const [surveyArea, setSurveyArea] = useState('');
  
  const [isFetchingAi, setIsFetchingAi] = useState(false);
  const [aiData, setAiData] = useState<{
    typicalSpecies: string[];
    soilConditions: string;
    weatherPatterns: string;
  } | null>(null);

  const handleAutoFetch = async () => {
    if (!siteAddress) return;
    setIsFetchingAi(true);
    try {
      const prompt = `Research the location "${siteAddress}" and provide typical tree species in that region, local soil/environmental conditions, and typical weather patterns. Return ONLY a JSON object with this structure: { "typicalSpecies": ["species 1", "species 2"], "soilConditions": "description", "weatherPatterns": "description" }`;
      
      // In a real app, this would use the Gemini API with search grounding.
      // We'll use the existing analyzeLocation function as a proxy for the AI call.
      const response = await analyzeLocation(prompt);
      
      try {
        // Try to parse the JSON response
        const jsonMatch = response.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          setAiData(parsed);
        }
      } catch (e) {
        console.error("Failed to parse AI response", e);
        // Fallback mock data if parsing fails
        setAiData({
          typicalSpecies: ['Oak', 'Maple', 'Pine'],
          soilConditions: 'Clay loam, moderately well-drained',
          weatherPatterns: 'Temperate, moderate rainfall'
        });
      }
    } catch (error) {
      console.error("Error fetching AI data", error);
    } finally {
      setIsFetchingAi(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onComplete({
      jobId: job?.id,
      clientName,
      clientPhone,
      clientEmail,
      surveyorName,
      purpose,
      siteAddress,
      surveyArea,
      date: new Date().toISOString(),
      aiSiteData: aiData || undefined,
      trees: job?.inventory || []
    });
  };

  return (
    <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[120] p-4">
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="bg-gecko p-6 text-white flex justify-between items-center sticky top-0 z-10">
          <div className="flex items-center space-x-3">
            <Icons.Tree className="w-6 h-6" />
            <h2 className="text-xl font-black uppercase tracking-widest">Tree Survey (BS5837)</h2>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition">
            <Icons.Close className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          
          {/* Customer & Surveyor Info */}
          <div>
            <h3 className="text-lg font-black text-bark mb-4 border-b border-stone-200 pb-2">Customer & Surveyor Info</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Client Name</label>
                <input required type="text" className="w-full p-3 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-medium text-bark" value={clientName} onChange={e => setClientName(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Client Phone</label>
                <input type="tel" className="w-full p-3 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-medium text-bark" value={clientPhone} onChange={e => setClientPhone(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Client Email</label>
                <input type="email" className="w-full p-3 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-medium text-bark" value={clientEmail} onChange={e => setClientEmail(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Surveyor Name</label>
                <input required type="text" className="w-full p-3 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-medium text-bark" value={surveyorName} onChange={e => setSurveyorName(e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Purpose of Survey</label>
                <input required type="text" placeholder="e.g. Planning Application, Risk Assessment" className="w-full p-3 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-medium text-bark" value={purpose} onChange={e => setPurpose(e.target.value)} />
              </div>
            </div>
          </div>

          {/* Site Location & AI Research */}
          <div>
            <h3 className="text-lg font-black text-bark mb-4 border-b border-stone-200 pb-2">Site Location & Area</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Job Site Address</label>
                <div className="flex space-x-2">
                  <input required type="text" className="flex-1 p-3 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-medium text-bark" value={siteAddress} onChange={e => setSiteAddress(e.target.value)} />
                  <button 
                    type="button"
                    onClick={handleAutoFetch}
                    disabled={isFetchingAi || !siteAddress}
                    className="px-4 bg-blue-50 text-blue-600 font-bold rounded-xl hover:bg-blue-100 transition disabled:opacity-50 flex items-center"
                  >
                    {isFetchingAi ? 'Fetching...' : 'Auto-fetch Data'}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Specific Area to Survey</label>
                <input required type="text" placeholder="e.g. Rear garden, North boundary" className="w-full p-3 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-medium text-bark" value={surveyArea} onChange={e => setSurveyArea(e.target.value)} />
              </div>
            </div>

            {aiData && (
              <div className="bg-blue-50 p-6 rounded-2xl border border-blue-100">
                <div className="flex items-center space-x-2 mb-4 text-blue-800">
                  <Icons.Tree className="w-5 h-5" />
                  <h4 className="font-black uppercase tracking-widest text-sm">AI Site Research</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                  <div>
                    <span className="font-bold text-blue-900 block mb-1">Typical Species</span>
                    <ul className="list-disc list-inside text-blue-800">
                      {aiData.typicalSpecies.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                  <div>
                    <span className="font-bold text-blue-900 block mb-1">Soil Conditions</span>
                    <p className="text-blue-800">{aiData.soilConditions}</p>
                  </div>
                  <div>
                    <span className="font-bold text-blue-900 block mb-1">Weather Patterns</span>
                    <p className="text-blue-800">{aiData.weatherPatterns}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="pt-4">
            <button type="submit" className="w-full py-4 bg-gecko text-white rounded-xl font-black uppercase tracking-widest hover:bg-palm transition shadow-lg shadow-gecko/20">
              Save Survey Data
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TreeSurvey;
